import {subledger} from './../profitandloss/api'
import {IBalanceSheetRow} from './types'

// Function to transform API response into table data
export const transformAssetsData = (assetsData: any): IBalanceSheetRow[] => {
  const result: IBalanceSheetRow[] = []

  // Process non-current assets
  if (assetsData['non-current']) {
    const nonCurrentGroup: IBalanceSheetRow = {
      id: 'non-current',
      parentGroup: 'Non-Current Assets',
      amount: assetsData['non-current'].value,
      group_id: assetsData['non-current'].group_id,
      children: [],
    }

    // Process sub-groups
    if (assetsData['non-current'].children && assetsData['non-current'].children.length > 0) {
      assetsData['non-current'].children.forEach((subGroup: any, index: number) => {
        const subGroupRow: IBalanceSheetRow = {
          id: `non-current-${index}`,
          subGroup: subGroup.sub_group || '-',
          amount: subGroup.value,
          group_id: subGroup.group_id,
          children: [],
        }

        // Process ledgers
        if (subGroup.children && subGroup.children.length > 0) {
          subGroup.children.forEach((ledger: any, ledgerIndex: number) => {
            const ledgerRow: IBalanceSheetRow = {
              id: `non-current-${index}-ledger-${ledgerIndex}`,
              parentLedger: ledger.parent_ledger,
              amount: ledger.value,
              ledger_id: ledger.ledger_id,
              children: [],
            }

            // Process sub-ledgers
            if (ledger.children && ledger.children.length > 0) {
              if (ledger.children.length === 1 && !ledger.children[0].sub_ledger) {
                ledgerRow.children = []
              } else {
                ledger.children.forEach((subLedger: any, subLedgerIndex: number) => {
                  ledgerRow.children?.push({
                    id: `non-current-${index}-ledger-${ledgerIndex}-subledger-${subLedgerIndex}`,
                    subLedger: subLedger.sub_ledger,
                    amount: subLedger.value,
                    ledger_id: subLedger.ledger_id,
                  })
                })
              }
            }

            subGroupRow.children?.push(ledgerRow)
          })
        }

        nonCurrentGroup.children?.push(subGroupRow)
      })
    }

    result.push(nonCurrentGroup)
  }

  // Process current assets
  if (assetsData['current']) {
    const currentGroup: IBalanceSheetRow = {
      id: 'current',
      parentGroup: 'Current Assets',
      amount: assetsData['current'].value,
      group_id: assetsData['current'].group_id,
      children: [],
    }

    // Process sub-groups (if any)
    if (assetsData['current'].children && assetsData['current'].children.length > 0) {
      assetsData['current'].children.forEach((subGroup: any, index: number) => {
        const subGroupRow: IBalanceSheetRow = {
          id: `current-${index}`,
          subGroup: subGroup.sub_group || '-',
          amount: subGroup.value,
          group_id: subGroup.group_id,
          children: [],
        }

        // Process ledgers
        if (subGroup.children && subGroup.children.length > 0) {
          subGroup.children.forEach((ledger: any, ledgerIndex: number) => {
            const ledgerRow: IBalanceSheetRow = {
              id: `current-${index}-ledger-${ledgerIndex}`,
              parentLedger: ledger.parent_ledger,
              amount: ledger.value,
              ledger_id: ledger.ledger_id,
              children: [],
            }

            // Process sub-ledgers
            if (ledger.children && ledger.children.length > 0) {
              if (ledger.children.length === 1 && !ledger.children[0].sub_ledger) {
                ledgerRow.children = []
              } else {
                ledger.children.forEach((subLedger: any, subLedgerIndex: number) => {
                  ledgerRow.children?.push({
                    id: `current-${index}-ledger-${ledgerIndex}-subledger-${subLedgerIndex}`,
                    subLedger: subLedger.sub_ledger,
                    amount: subLedger.value,
                    ledger_id: subLedger.ledger_id,
                  })
                })
              }
            }

            subGroupRow.children?.push(ledgerRow)
          })
        }

        currentGroup.children?.push(subGroupRow)
      })
    }

    result.push(currentGroup)
  }

  return result
}

export const transformLiabilitiesData = (liabilityData: any): IBalanceSheetRow[] => {
  const result: IBalanceSheetRow[] = []

  // Process current liabilities
  if (liabilityData['current']) {
    const currentGroup: IBalanceSheetRow = {
      id: 'liability-current',
      parentGroup: 'Current Liabilities',
      amount: liabilityData['current'].value,
      group_id: liabilityData['current'].group_id,
      children: [],
    }

    // Process sub-groups
    if (liabilityData['current'].children && liabilityData['current'].children.length > 0) {
      liabilityData['current'].children.forEach((subGroup: any, index: number) => {
        const subGroupRow: IBalanceSheetRow = {
          id: `liability-current-${index}`,
          subGroup: subGroup.sub_group || '-',
          amount: subGroup.value,
          group_id: subGroup.group_id,
          children: [],
        }

        // Process ledgers
        if (subGroup.children && subGroup.children.length > 0) {
          subGroup.children.forEach((ledger: any, ledgerIndex: number) => {
            const ledgerRow: IBalanceSheetRow = {
              id: `liability-current-${index}-ledger-${ledgerIndex}`,
              parentLedger: ledger.parent_ledger,
              amount: ledger.value,
              ledger_id: ledger.ledger_id,
              children: [],
            }

            // Process sub-ledgers
            if (ledger.children && ledger.children.length > 0) {
              if (ledger.children.length === 1 && !ledger.children[0].sub_ledger) {
                ledgerRow.children = []
              } else {
                ledger.children.forEach((subLedger: any, subLedgerIndex: number) => {
                  ledgerRow.children?.push({
                    id: `liability-current-${index}-ledger-${ledgerIndex}-subledger-${subLedgerIndex}`,
                    subLedger: subLedger.sub_ledger,
                    amount: subLedger.value,
                    ledger_id: subLedger.ledger_id,
                  })
                })
              }
            }

            subGroupRow.children?.push(ledgerRow)
          })
        }

        currentGroup.children?.push(subGroupRow)
      })
    }

    result.push(currentGroup)
  }

  // Process non-current liabilities
  if (liabilityData['non-current']) {
    const nonCurrentGroup: IBalanceSheetRow = {
      id: 'liability-non-current',
      parentGroup: 'Non-Current Liabilities',
      amount: liabilityData['non-current'].value,
      group_id: liabilityData['non-current'].group_id,
      children: [],
    }

    // Process sub-groups
    if (liabilityData['non-current'].children && liabilityData['non-current'].children.length > 0) {
      liabilityData['non-current'].children.forEach((subGroup: any, index: number) => {
        const subGroupRow: IBalanceSheetRow = {
          id: `liability-non-current-${index}`,
          subGroup: subGroup.sub_group || '-',
          amount: subGroup.value,
          group_id: subGroup.group_id,
          children: [],
        }

        // Process ledgers
        if (subGroup.children && subGroup.children.length > 0) {
          subGroup.children.forEach((ledger: any, ledgerIndex: number) => {
            const ledgerRow: IBalanceSheetRow = {
              id: `liability-non-current-${index}-ledger-${ledgerIndex}`,
              parentLedger: ledger.parent_ledger,
              amount: ledger.value,
              ledger_id: ledger.ledger_id,
              children: [],
            }

            // Process sub-ledgers
            if (ledger.children && ledger.children.length > 0) {
              if (ledger.children.length === 1 && !ledger.children[0].sub_ledger) {
                ledgerRow.children = []
              } else {
                ledger.children.forEach((subLedger: any, subLedgerIndex: number) => {
                  ledgerRow.children?.push({
                    id: `liability-non-current-${index}-ledger-${ledgerIndex}-subledger-${subLedgerIndex}`,
                    subLedger: subLedger.sub_ledger,
                    amount: subLedger.value,
                    ledger_id: subLedger.ledger_id,
                  })
                })
              }
            }

            subGroupRow.children?.push(ledgerRow)
          })
        }

        nonCurrentGroup.children?.push(subGroupRow)
      })
    }

    result.push(nonCurrentGroup)
  }

  return result
}

export const transformEquityData = (liabilityData: any): IBalanceSheetRow[] => {
  const result: IBalanceSheetRow[] = []

  // Process equity
  if (liabilityData['equity']) {
    const equityGroup: IBalanceSheetRow = {
      id: 'equity',
      parentGroup: 'Equity',
      amount: liabilityData['equity'].value,
      group_id: liabilityData['equity'].group_id,
      children: [],
    }

    // Process sub-groups
    if (liabilityData['equity'].children && liabilityData['equity'].children.length > 0) {
      liabilityData['equity'].children.forEach((subGroup: any, index: number) => {
        const subGroupRow: IBalanceSheetRow = {
          id: `equity-${index}`,
          subGroup: subGroup.sub_group || '-',
          amount: subGroup.value,
          group_id: subGroup.group_id,
          children: [],
        }

        // Process ledgers
        if (subGroup.children && subGroup.children.length > 0) {
          subGroup.children.forEach((ledger: any, ledgerIndex: number) => {
            const ledgerRow: IBalanceSheetRow = {
              id: `equity-${index}-ledger-${ledgerIndex}`,
              parentLedger: ledger.parent_ledger,
              amount: ledger.value,
              ledger_id: ledger.ledger_id,
              children: [],
            }

            // Process sub-ledgers
            if (ledger.children && ledger.children.length > 0) {
              if (ledger.children.length === 1 && !ledger.children[0].sub_ledger) {
                ledgerRow.children = []
              } else {
                ledger.children.forEach((subLedger: any, subLedgerIndex: number) => {
                  ledgerRow.children?.push({
                    id: `equity-${index}-ledger-${ledgerIndex}-subledger-${subLedgerIndex}`,
                    subLedger: subLedger.sub_ledger,
                    amount: subLedger.value,
                    ledger_id: subLedger.ledger_id,
                  })
                })
              }
            }
            subGroupRow.children?.push(ledgerRow)
          })
        }

        equityGroup.children?.push(subGroupRow)
      })
    }

    result.push(equityGroup)
  }

  return result
}

// Format currency value for display
export const formatCurrency = (value: number): string => {
  return (
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
      notation: 'standard',
    })
      .format(value)
      .replace('₹', '') + ' INR'
  )
}
